/**
 * Zod schemas for shipping label API endpoint
 * Shared between client and server code
 */

import { z } from 'zod';

// Zod schema for label request (query params)
export const ShippingLabelRequestSchema = z.object({
  id: z.string().min(1, 'Shipping ID is required'),
});

export type ShippingLabelRequest = z.infer<typeof ShippingLabelRequestSchema>;

// Zod schema for label response
export const ShippingLabelResponseSchema = z.object({
  success: z.literal(true),
  labelUrl: z.string(),
});

export type ShippingLabelResponse = z.infer<typeof ShippingLabelResponseSchema>;

// Error response schema
export const ShippingLabelErrorResponseSchema = z.object({
  success: z.literal(false),
  error: z.string(),
  /**
   * Set when the label is missing because the carrier refused the purchase, not
   * because none was due yet. The client buys it instead of stopping.
   */
  recoverable: z.boolean().optional(),
});

// Zod schema for the retry-label response
export const RetryShippingLabelResponseSchema = z.discriminatedUnion('success', [
  z.object({
    success: z.literal(true),
    labelUrl: z.string(),
    trackingNumber: z.string().nullable(),
  }),
  z.object({
    success: z.literal(false),
    error: z.string(),
  }),
]);

export type RetryShippingLabelResponse = z.infer<typeof RetryShippingLabelResponseSchema>;

export type ShippingLabelErrorResponse = z.infer<typeof ShippingLabelErrorResponseSchema>;

// Combined response schema (discriminated union)
export const GetShippingLabelResponseSchema = z.discriminatedUnion('success', [
  ShippingLabelResponseSchema,
  ShippingLabelErrorResponseSchema,
]);

export type GetShippingLabelResponse = z.infer<typeof GetShippingLabelResponseSchema>;


/**
 * What a caller gets back from fetching — or buying — a label.
 *
 * `recoverable` marks the one failure the client can act on: the carrier
 * refused the purchase, so buying it now will produce a label.
 */
export type ShippingLabelFailure = { success: false; error: string; recoverable?: boolean };
export type ShippingLabelResult = { success: true; labelUrl: string } | ShippingLabelFailure;

/**
 * Narrow a result to its failure arm.
 *
 * The project compiles with `strict: false`, and TypeScript only narrows a
 * union on a boolean discriminant under strictNullChecks — so a plain
 * `if (!result.success)` does not compile here even though `success` is a
 * literal type on both arms. Same reason `isAdminApiAuthDenied` exists.
 */
export function isShippingLabelFailure(result: ShippingLabelResult): result is ShippingLabelFailure {
  return !result.success;
}

/**
 * Rates for an order whose label was never bought.
 *
 * The seller is choosing, not the code: the buyer paid a flat estimate at
 * checkout and the real rates can land either side of it, so the difference is
 * theirs to accept before anything is purchased. `paidAmount` is what the buyer
 * was charged for shipping, carried alongside the rates so the UI can show the
 * comparison rather than making the seller do the arithmetic.
 */
export const LabelRateSchema = z.object({
  id: z.string(),
  provider: z.string(),
  service: z.string(),
  amount: z.number(),
  estimatedDays: z.number().nullable(),
});

export type LabelRate = z.infer<typeof LabelRateSchema>;

/**
 * Why the seller cannot be quoted, when they cannot be.
 *
 * `no_seller_address` is the one worth naming: it is not an error so much as an
 * unfinished setup, and the client turns it into "add the address you ship
 * from" rather than a red failure toast. `no_seller_name` is the same shape of
 * problem — USPS refuses a return address with no name, and refuses it at
 * purchase, so it is caught before the seller picks a rate.
 */
export const LabelQuoteBlockerSchema = z.enum([
  'no_seller_address',
  'no_seller_name',
  'no_buyer_address',
  'not_quotable',
]);

export type LabelQuoteBlocker = z.infer<typeof LabelQuoteBlockerSchema>;

export const LabelQuoteResponseSchema = z.discriminatedUnion('success', [
  z.object({
    success: z.literal(true),
    rates: z.array(LabelRateSchema),
    paidAmount: z.number(),
  }),
  z.object({
    success: z.literal(false),
    error: z.string(),
    blocker: LabelQuoteBlockerSchema.optional(),
  }),
]);

export type LabelQuoteResponse = z.infer<typeof LabelQuoteResponseSchema>;

export type LabelQuoteSuccess = Extract<LabelQuoteResponse, { success: true }>;
export type LabelQuoteFailure = Extract<LabelQuoteResponse, { success: false }>;

/** Same `strict: false` narrowing problem as `isShippingLabelFailure`. */
export function isLabelQuoteFailure(result: LabelQuoteResponse): result is LabelQuoteFailure {
  return !result.success;
}

/**
 * GET /api/shipping/[id]/qr-code: the USPS QR code for a bought label, so the
 * seller can have the label printed at the Post Office (utils/usps-qr.ts).
 * `qrCodeUrl` is null when the label has none: not USPS, or bought before
 * 2026-10-11 when labels were bought without one.
 */
export const ShippingQrCodeResponseSchema = z.discriminatedUnion('success', [
  z.object({
    success: z.literal(true),
    qrCodeUrl: z.string().nullable(),
  }),
  z.object({
    success: z.literal(false),
    error: z.string(),
  }),
]);

export type ShippingQrCodeResponse = z.infer<typeof ShippingQrCodeResponseSchema>;
